import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, Calendar, Tag as TagIcon, Clock } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { PresaleDialogProvider } from "@/components/sections/PresaleDialog";
import { getEditorialPost } from "@/content/editorialPosts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Estimate read time from stripped HTML at 220 wpm.
function readTimeMinutes(html) {
  if (!html) return 1;
  const words = html.replace(/<[^>]+>/g, " ").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

// Right-to-left language codes.
const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);

// HeyCatch article body — sanitises content_html, inserts a Table of
// contents (right after the Key takeaways list, or at the top otherwise),
// and applies lang/dir on the wrapper. Separated from BlogPost so the
// prose rendering stays declarative.
function HeyCatchArticleBody({ post, toc }) {
  const lang = post.language || "en";
  const dir = RTL_LANGS.has(lang) ? "rtl" : undefined;

  // Sanitise once. Keep the <h2 id> attribute so TOC anchors work.
  const cleanHtml = DOMPurify.sanitize(post.content_html || "", {
    ADD_ATTR: ["id"],
  });

  // Build a TOC fragment (string) once from the items we already parsed.
  const tocHtml = toc.items.length
    ? `
      <nav aria-label="Table of contents" class="blog-toc" data-testid="blog-post-toc">
        <h2>Table of contents</h2>
        <ol>
          ${toc.items
            .map(
              (it) =>
                `<li><a href="#${it.id}">${it.text.replace(/</g, "&lt;")}</a></li>`
            )
            .join("")}
        </ol>
      </nav>`
    : "";

  // Insert the TOC into the HTML at the correct spot. Guide: right after
  // the Key takeaways list (the <ul>/<ol> that follows <h2>Key takeaways</h2>).
  // No takeaways? Prepend above content_html.
  let finalHtml = cleanHtml;
  if (tocHtml) {
    if (toc.hasKeyTakeaways) {
      // Match "<h2>Key takeaways</h2>" (case-insensitive, allow attrs)
      // then the next <ul> or <ol>…</ul|ol>, and inject the TOC after it.
      const re =
        /(<h2\b[^>]*>\s*key takeaways\s*<\/h2>\s*(?:<(ul|ol)\b[^>]*>[\s\S]*?<\/\2>))/i;
      if (re.test(finalHtml)) {
        finalHtml = finalHtml.replace(re, (m) => m + tocHtml);
      } else {
        finalHtml = tocHtml + finalHtml;
      }
    } else {
      finalHtml = tocHtml + finalHtml;
    }
  }

  return (
    <article
      data-testid="blog-post-content"
      className="blog-prose"
      lang={lang}
      {...(dir ? { dir } : {})}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");
  // HeyCatch t_orphan_pages — "Related articles" sibling block.
  // Populated from /api/blog/posts (same list the prerender uses) and
  // scored on shared tags → shared slug tokens. Never fabricated.
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setError("");
    setRelated([]);
    const editorialPost = getEditorialPost(slug);
    const request = editorialPost
      ? Promise.resolve({ data: editorialPost })
      : axios.get(`${API}/blog/posts/${slug}`);

    request.then(({ data }) => {
        setPost(data);
        document.title = `${data.title} — Zynthoro`;
        // Update meta description for SEO. For HeyCatch posts the guide
        // says meta_description goes into <meta>/OG tags only — not
        // rendered as visible text (handled below in the body).
        const desc =
          (data.source === "heycatch" ? data.meta_description : "") ||
          data.excerpt ||
          (data.content_markdown || "").slice(0, 160);
        let tag = document.querySelector('meta[name="description"]');
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("name", "description");
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", desc);

        // Inject Open Graph + Twitter card tags per post. Turns every
        // share on LinkedIn/Slack/WhatsApp/X into a rich preview card.
        // Uses a shared id prefix so we can nuke them cleanly on unmount.
        const canonicalUrl = `${window.location.origin}/blog/${data.slug}`;
        const ogTags = [
          ["property", "og:type", "article"],
          ["property", "og:title", data.title],
          ["property", "og:description", desc],
          ["property", "og:url", canonicalUrl],
          ...(data.cover_image_url ? [["property", "og:image", data.cover_image_url]] : []),
          ["property", "article:published_time", data.published_at || ""],
          ["property", "article:modified_time", data.updated_at || data.published_at || ""],
          ["property", "article:author", "Zynthoro"],
          ["name", "twitter:card", data.cover_image_url ? "summary_large_image" : "summary"],
          ["name", "twitter:title", data.title],
          ["name", "twitter:description", desc],
          ...(data.cover_image_url ? [["name", "twitter:image", data.cover_image_url]] : []),
        ];
        // Remove any previous per-post tags first
        document
          .querySelectorAll('meta[data-blog-og="1"]')
          .forEach((n) => n.remove());
        ogTags.forEach(([attr, key, value]) => {
          if (value === undefined || value === null) return;
          const m = document.createElement("meta");
          m.setAttribute(attr, key);
          m.setAttribute("content", value);
          m.setAttribute("data-blog-og", "1");
          document.head.appendChild(m);
        });

        // Inject Article JSON-LD schema — helps Google index each post as
        // a rich result. Removed on unmount so we don't leak across posts.
        // HeyCatch posts carry their own jsonLd — prefer it when present
        // (guide: set url/mainEntityOfPage to canonical first).
        const ldTag = document.createElement("script");
        ldTag.type = "application/ld+json";
        ldTag.id = "blog-article-jsonld";
        const canonical = `${window.location.origin}/blog/${data.slug}`;
        let ldPayload;
        if (
          data.source === "heycatch" &&
          data.jsonLd &&
          typeof data.jsonLd === "object" &&
          Object.keys(data.jsonLd).length > 0
        ) {
          ldPayload = { ...data.jsonLd };
          if (!ldPayload.url) ldPayload.url = canonical;
          if (!ldPayload.mainEntityOfPage)
            ldPayload.mainEntityOfPage = { "@type": "WebPage", "@id": canonical };
        } else {
          ldPayload = {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: data.title,
            description: desc,
            image: data.cover_image_url ? [data.cover_image_url] : undefined,
            datePublished: data.published_at,
            dateModified: data.updated_at || data.published_at,
            author: { "@type": "Organization", name: "Zynthoro" },
            publisher: {
              "@type": "Organization",
              name: "Zynthoro",
              logo: {
                "@type": "ImageObject",
                url: `${window.location.origin}/logo.png`,
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            url: canonical,
            keywords: Array.isArray(data.tags) ? data.tags.join(", ") : undefined,
          };
        }
        // Guide: serialise with every "<" escaped as \u003c.
        ldTag.textContent = JSON.stringify(ldPayload).replace(/</g, "\\u003c");
        // Remove any previous instance before appending.
        document.getElementById("blog-article-jsonld")?.remove();
        document.head.appendChild(ldTag);

        // Canonical URL — point search engines at the production domain
        // if PUBLIC_SITE_URL is set, otherwise this origin.
        let canonicalTag = document.querySelector('link[rel="canonical"]');
        if (!canonicalTag) {
          canonicalTag = document.createElement("link");
          canonicalTag.setAttribute("rel", "canonical");
          document.head.appendChild(canonicalTag);
        }
        canonicalTag.setAttribute("href", canonical);

        // HeyCatch t_orphan_pages — fetch sibling posts for the
        // "Related articles" block. Scored by shared tags, then by
        // shared slug/title tokens. Deterministic, never fabricated.
        // Skipped for the in-code editorial post because its slug is
        // not in the API list; it stays reachable from /blog instead.
        if (!editorialPost) {
          axios
            .get(`${API}/blog/posts`, { params: { limit: 200 } })
            .then((r) => {
              const all = (r.data && r.data.posts) || [];
              const tokenise = (s) =>
                String(s || "")
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, " ")
                  .split(/\s+/)
                  .filter((w) => w.length > 3);
              const curTags = new Set(
                (Array.isArray(data.tags) ? data.tags : []).map((t) =>
                  String(t).toLowerCase().trim()
                )
              );
              const curTokens = new Set(tokenise(data.slug + " " + data.title));
              const scored = [];
              for (const p of all) {
                if (!p || !p.slug || p.slug === data.slug) continue;
                const tags = (Array.isArray(p.tags) ? p.tags : []).map((t) =>
                  String(t).toLowerCase().trim()
                );
                let tagHits = 0;
                for (const t of tags) if (curTags.has(t)) tagHits++;
                let tokenHits = 0;
                if (!tagHits) {
                  for (const w of tokenise(p.slug + " " + p.title))
                    if (curTokens.has(w)) tokenHits++;
                }
                const score = tagHits * 10 + tokenHits;
                if (score <= 0) continue;
                scored.push({
                  post: p,
                  score,
                  when: p.updated_at || p.published_at || "",
                });
              }
              scored.sort(
                (a, b) =>
                  b.score - a.score ||
                  String(b.when).localeCompare(String(a.when))
              );
              let picks = scored.slice(0, 4).map((s) => s.post);
              // t_orphan_pages — fallback to the 4 most-recent OTHER
              // posts when the topical scorer finds zero signal, so no
              // article renders without a Related block.
              if (picks.length === 0) {
                picks = all
                  .filter((p) => p && p.slug && p.slug !== data.slug)
                  .slice()
                  .sort((a, b) =>
                    String(b.updated_at || b.published_at || "").localeCompare(
                      String(a.updated_at || a.published_at || "")
                    )
                  )
                  .slice(0, 4);
              }
              setRelated(picks);
            })
            .catch(() => { /* non-fatal — related is best-effort */ });
        }
      }).catch((e) => {
        if (e?.response?.status === 404) {
          setError("This article doesn't exist or was removed.");
          document.title = "Article not found — Zynthoro";
        } else {
          setError("Couldn't load this article. Please refresh.");
        }
      });

    return () => {
      // Cleanup: strip the JSON-LD + per-post OG/Twitter tags when
      // navigating away, so the next page doesn't inherit them.
      document.getElementById("blog-article-jsonld")?.remove();
      document
        .querySelectorAll('meta[data-blog-og="1"]')
        .forEach((n) => n.remove());
    };
  }, [slug]);

  const readMins = post
    ? (post.source === "heycatch" && post.read_time) || readTimeMinutes(post.content_html || post.content_markdown)
    : 0;

  // HeyCatch guide step 3: build a table of contents from every <h2> in
  // content_html that has an id, except id="faq". Also detect whether the
  // first <h2> is "Key takeaways" so we can insert the TOC right after it.
  // Done deterministically from the sanitised HTML we actually render.
  const toc = (() => {
    if (!post || post.source !== "heycatch" || !post.content_html) {
      return { items: [], insertAfterIndex: -1 };
    }
    try {
      const safe = DOMPurify.sanitize(post.content_html, { ADD_ATTR: ["id"] });
      const doc = new DOMParser().parseFromString(safe, "text/html");
      const headings = Array.from(doc.body.querySelectorAll("h2"));
      const items = [];
      let keyTakeawaysIdx = -1;
      headings.forEach((h, i) => {
        const text = (h.textContent || "").trim();
        const id = h.getAttribute("id");
        if (!id || id === "faq") {
          if (text.toLowerCase() === "key takeaways") keyTakeawaysIdx = i;
          return;
        }
        if (text.toLowerCase() === "key takeaways") return;
        items.push({ id, text });
      });
      return { items, hasKeyTakeaways: keyTakeawaysIdx >= 0 };
    } catch {
      return { items: [], hasKeyTakeaways: false };
    }
  })();

  return (
    <PresaleDialogProvider>
      <Navbar />

      {/* Hero */}
      <section
        style={{ background: "var(--zy-blue)" }}
        className="text-white"
        data-testid="blog-post-hero"
      >
        <div className="zy-container py-14 md:py-20 max-w-[860px]">
          {/* HeyCatch t_orphan_pages — visible breadcrumb trail. Also
              feeds the BreadcrumbList JSON-LD emitted by the prerender. */}
          <nav
            aria-label="Breadcrumb"
            data-testid="blog-post-breadcrumb"
            className="text-[12.5px] text-white/70 mb-5"
          >
            <ol className="inline-flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-white underline-offset-4 hover:underline">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" className="text-white/40">/</li>
              <li>
                <Link to="/blog" className="hover:text-white underline-offset-4 hover:underline">
                  Blog
                </Link>
              </li>
              {post && (
                <>
                  <li aria-hidden="true" className="text-white/40">/</li>
                  <li className="text-white/90 line-clamp-1 max-w-[320px]">
                    {post.title}
                  </li>
                </>
              )}
            </ol>
          </nav>

          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[13px] mb-6 transition-colors"
          >
            <ArrowLeft size={14} />
            Back to blog
          </Link>

          {post ? (
            <>
              <p
                className="uppercase tracking-[0.18em] text-[12px] mb-3"
                style={{ color: "var(--zy-gold)" }}
              >
                Zynthoro · Blog
              </p>
              <h1
                data-testid="blog-post-title"
                className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.15]"
              >
                {post.title}
              </h1>
              <div
                data-testid="blog-post-meta"
                className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13.5px] text-white/75"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={14} />
                  Published {formatDate(post.published_at)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={14} />
                  {readMins} min read
                </span>
                {Array.isArray(post.tags) && post.tags.length > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <TagIcon size={14} />
                    {post.tags.slice(0, 4).join(" · ")}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="animate-pulse">
              <div className="h-3 w-32 bg-white/20 rounded mb-4" />
              <div className="h-10 w-3/4 bg-white/20 rounded mb-3" />
              <div className="h-10 w-1/2 bg-white/20 rounded" />
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="bg-white">
        <div className="zy-container py-12 md:py-16 max-w-[860px]">
          {error && (
            <div
              data-testid="blog-post-error"
              className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-[14px]"
            >
              {error}
              <div className="mt-3">
                <Link
                  to="/blog"
                  className="text-[var(--zy-blue)] font-semibold text-[14px] inline-flex items-center gap-1.5"
                >
                  <ArrowLeft size={14} /> Back to all articles
                </Link>
              </div>
            </div>
          )}

          {!error && !post && (
            <div data-testid="blog-post-loading" className="animate-pulse space-y-4">
              <div className="h-4 w-full bg-black/[0.06] rounded" />
              <div className="h-4 w-11/12 bg-black/[0.06] rounded" />
              <div className="h-4 w-10/12 bg-black/[0.06] rounded" />
              <div className="h-4 w-9/12 bg-black/[0.06] rounded" />
            </div>
          )}

          {post && (
            <>
              {post.cover_image_url && (
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  data-testid="blog-post-cover"
                  className="w-full rounded-2xl mb-10 shadow-[0_25px_60px_-30px_rgba(10,22,40,0.45)]"
                  style={{ aspectRatio: "16 / 9", objectFit: "cover" }}
                  loading="eager"
                  decoding="async"
                />
              )}

              {/* HeyCatch g_answer_first — a visible, answer-first lede
                  paragraph placed above the article body. Sourced from
                  the post's own excerpt (or the first paragraph of its
                  markdown as a fallback) so it never invents claims.
                  Skipped for HeyCatch posts: the guide forbids any
                  subtitle/visible meta_description under the title. */}
              {post.source !== "heycatch" && (() => {
                const lede =
                  (post.excerpt && String(post.excerpt).trim()) ||
                  String(post.content_markdown || "")
                    .split(/\n\s*\n/)[0]
                    .replace(/\s+/g, " ")
                    .trim()
                    .slice(0, 320);
                if (!lede) return null;
                return (
                  <p
                    data-testid="blog-post-answer-first"
                    data-answer-first="1"
                    className="text-[17px] md:text-[18px] leading-relaxed font-semibold text-[#0A1628] mb-8"
                  >
                    {lede}
                  </p>
                );
              })()}

              {post.source === "heycatch" ? (
                <HeyCatchArticleBody post={post} toc={toc} />
              ) : (
                <article
                  data-testid="blog-post-content"
                  className="blog-prose"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content_html || "") }}
                />
              )}
              {/* HeyCatch t_orphan_pages — "Related" sibling block,
                  visible in the runtime UI. Shape matches the audit's
                  suggested patch (aside aria-label="Related" > h2
                  "Related" > ul > li > a) so the HeyCatch extractor
                  recognises it. Only renders when the deterministic
                  scorer actually found related posts. */}
              {related.length > 0 && (
                <aside
                  data-testid="blog-post-related"
                  aria-label="Related"
                  className="mt-14 pt-8 border-t border-black/[0.08]"
                >
                  <h2 className="text-[18px] font-semibold text-[#0A1628] mb-4">
                    Related
                  </h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[14.5px]">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link
                          to={`/blog/${r.slug}`}
                          className="block rounded-xl ring-1 ring-black/[0.06] p-3.5 hover:ring-[var(--zy-blue)]/40 transition"
                        >
                          <span className="block font-semibold text-[#0A1628] line-clamp-2">
                            {r.title}
                          </span>
                          {r.excerpt && (
                            <span className="block text-black/55 mt-1 text-[13px] line-clamp-2">
                              {r.excerpt}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </aside>
              )}

              <div className="mt-14 pt-8 border-t border-black/[0.08] flex items-center justify-between text-[13.5px]">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-1.5 text-[var(--zy-blue)] font-semibold hover:underline"
                >
                  <ArrowLeft size={14} /> All articles
                </Link>
                <span className="text-black/45">
                  Last updated {formatDate(post.updated_at)}
                </span>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </PresaleDialogProvider>
  );
}
